import React, { useEffect, useRef, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Accessibility as AccessibilityIcon,
  Woman as WomanIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  Location,
  OpeningHourCategories,
  OpeningHourCategory,
  OpeningHoursEntry,
  OpeningTime,
} from '../../types/Location';
import { serializeOpeningTimes } from '../../utils/openingTimeUtils';
import OpeningHoursTable, { OpeningHoursTableRef } from './OpeningHoursTable';
import { GeocodingService, AddressComponents } from '../../utils/geocodingService';
import { normalizeDayValue } from '../../utils/dayUtils';
import { getCategoryIcon } from '../../utils/categoryIcons';
import ImageUpload from '../ImageUpload';
import LocalizedTextFields from '../common/LocalizedTextFields';
import { emptyLocalizedText } from '../../types/LocalizedText';
import { useLanguages } from '../../hooks/useLanguages';
import {
  getLocalizedText,
  hasLocalizedText,
  normalizeLocalizedText,
  serializeLocalizedText,
} from '../../utils/localizedText';

type LocationFormData = Omit<Location, 'createdAt' | 'updatedAt' | 'createdBy' | 'deletedAt'> & {
  donationAccount?: string;
};

function normalizeOpeningHoursEntries(
  entries: OpeningTime[] | undefined
): OpeningHoursEntry[] {
  if (!entries) {
    return [];
  }

  return entries.map((entry) => ({
    ...entry,
    id: uuidv4(),
    day: normalizeDayValue(entry.day),
  }));
}

function normalizeCategories(
  categories: OpeningHourCategories | undefined
): OpeningHourCategories {
  if (!categories) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(categories).map(([key, entries]) => [
      key,
      normalizeOpeningHoursEntries(entries),
    ])
  );
}

const LocationForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditMode = !!id;
  const { activeLanguages, languageCodes } = useLanguages();

  const { data: location, isLoading: locationLoading } = useQuery({
    queryKey: ['location', id],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/locations/${id}`);
      return response.data as Location;
    },
    enabled: isEditMode,
  });

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery<OpeningHourCategory[]>({
    queryKey: ['openingHourCategories', 'active'],
    queryFn: async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/opening-hour-categories`
      );
      return response.data as OpeningHourCategory[];
    },
  });

  const activeCategories = useMemo<OpeningHourCategory[]>(() => {
    const categories = categoriesData ?? [];
    return [...categories]
      .filter((category) => category.isActive !== false)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [categoriesData]);

  const { control, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<LocationFormData>({
    defaultValues: {
      name: '',
      street: '',
      zip: '',
      city: '',
      country: 'Deutschland',
      coordinates: {
        lat: 0,
        lng: 0,
      },
      phone: '',
      email: '',
      website: '',
      description: emptyLocalizedText(languageCodes),
      donationAccount: '',
      image: undefined,
      isAccessible: false,
      offersForWomen: false,
      categories: {},
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const [isInitialized, setIsInitialized] = React.useState(false);
  const [geocodingError, setGeocodingError] = React.useState<string | null>(null);
  const openingHoursRefs = useRef<Record<string, OpeningHoursTableRef | null>>({});

  useEffect(() => {
    if (!isEditMode) {
      setIsInitialized(false);
      reset({
        name: '',
        street: '',
        zip: '',
        city: '',
        country: 'Deutschland',
        coordinates: { lat: 0, lng: 0 },
        phone: '',
        email: '',
        website: '',
        description: emptyLocalizedText(languageCodes),
        donationAccount: '',
        image: undefined,
        isAccessible: false,
        offersForWomen: false,
        categories: {},
      });
    }
  }, [isEditMode, reset, languageCodes]);

  const geocodeAddress = React.useCallback(async () => {
    const street = watch('street') || '';
    const zip = watch('zip') || '';
    const city = watch('city') || '';
    const country = watch('country') || 'Deutschland';

    const addressComponents: AddressComponents = { street, zip, city, country };
    const coordinates = await GeocodingService.geocodeAddress(addressComponents);

    if (coordinates) {
      setValue('coordinates.lat', coordinates.lat);
      setValue('coordinates.lng', coordinates.lng);
    }
  }, [watch, setValue]);

  useEffect(() => {
    if (location && !isInitialized && location.id) {
      const imageValue =
        location.id && location.hasImage === true
          ? `${process.env.REACT_APP_API_URL}/locations/${location.id}/image`
          : undefined;
      const finalImageValue = location.hasImage === false ? undefined : imageValue;
      const { image: _, ...locationWithoutImage } = location;

      reset({
        ...locationWithoutImage,
        image: finalImageValue,
        categories: normalizeCategories(location.categories),
        isAccessible: location.isAccessible || false,
        offersForWomen: location.offersForWomen || false,
        description: normalizeLocalizedText(location.description, languageCodes),
        donationAccount: location.donationAccount || '',
      });

      setIsInitialized(true);
    }
  }, [location, reset, isInitialized, languageCodes]);

  const mutation = useMutation({
    mutationFn: async (data: LocationFormData) => {
      const formData = new FormData();

      formData.append('name', data.name);
      formData.append('street', data.street);
      formData.append('zip', data.zip);
      formData.append('city', data.city);
      formData.append('country', data.country || 'Deutschland');
      formData.append('coordinates', JSON.stringify(data.coordinates));

      if (data.phone) formData.append('phone', data.phone);
      if (data.email) formData.append('email', data.email);
      if (data.website) formData.append('website', data.website);
      if (hasLocalizedText(data.description)) {
        formData.append(
          'description',
          JSON.stringify(
            serializeLocalizedText(data.description ?? emptyLocalizedText(languageCodes), activeLanguages, {
              omitEmptyOptional: true,
            })
          )
        );
      }
      if (data.donationAccount) formData.append('donationAccount', data.donationAccount);

      formData.append('isAccessible', data.isAccessible ? 'true' : 'false');
      formData.append('offersForWomen', data.offersForWomen ? 'true' : 'false');

      if (data.image instanceof File) {
        formData.append('image', data.image);
      }

      formData.append('categories', JSON.stringify(data.categories || {}));

      if (isEditMode) {
        return axios.put(`${process.env.REACT_APP_API_URL}/locations/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      return axios.post(`${process.env.REACT_APP_API_URL}/locations`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      if (isEditMode && id) {
        queryClient.invalidateQueries({ queryKey: ['location', id] });
      }
      navigate('/locations');
    },
    onError: (error) => {
      console.error('Mutation error:', error);
    },
  });

  const onSubmit = async (data: LocationFormData) => {
    const addressComponents: AddressComponents = {
      street: data.street || '',
      zip: data.zip || '',
      city: data.city || '',
      country: data.country || 'Deutschland',
    };

    if (!GeocodingService.isValidCoordinates(data.coordinates)) {
      const coordinates = await GeocodingService.geocodeAddress(addressComponents);

      if (coordinates) {
        data.coordinates = coordinates;
        setGeocodingError(null);
      } else {
        const errorMessage = 'Failed to geocode address. Please check the address and try again.';
        console.error(errorMessage);
        setGeocodingError(errorMessage);
        return;
      }
    }

    const categories: OpeningHourCategories = {};
    activeCategories.forEach((category) => {
      const entries = openingHoursRefs.current[category.key]?.getCurrentValue() || [];
      categories[category.key] = serializeOpeningTimes(entries, activeLanguages);
    });

    mutation.mutate({
      ...data,
      categories,
    });
  };

  useEffect(() => {
    const handleSubmitCapture = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('submit', handleSubmitCapture, true);

    return () => {
      document.removeEventListener('submit', handleSubmitCapture, true);
    };
  }, []);

  if ((isEditMode && locationLoading) || categoriesLoading) {
    return <Typography>{t('locationForm.loading')}</Typography>;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        {isEditMode ? t('locationForm.editLocation') : t('locationForm.addLocation')}
      </Typography>

      {geocodingError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {geocodingError}
        </Typography>
      )}

      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Controller
              name="name"
              control={control}
              rules={{ required: t('locationForm.nameRequired') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('locationForm.name')}
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="street"
              control={control}
              rules={{ required: t('locationForm.streetRequired') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('locationForm.street')}
                  fullWidth
                  error={!!errors.street}
                  helperText={errors.street?.message}
                  onBlur={(e) => {
                    field.onBlur();
                    setGeocodingError(null);
                    geocodeAddress();
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="zip"
              control={control}
              rules={{ required: t('locationForm.zipCodeRequired') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('locationForm.zipCode')}
                  fullWidth
                  error={!!errors.zip}
                  helperText={errors.zip?.message}
                  onBlur={(e) => {
                    field.onBlur();
                    setGeocodingError(null);
                    geocodeAddress();
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="city"
              control={control}
              rules={{ required: t('locationForm.cityRequired') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('locationForm.city')}
                  fullWidth
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  onBlur={(e) => {
                    field.onBlur();
                    setGeocodingError(null);
                    geocodeAddress();
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('locationForm.country')}
                  fullWidth
                  defaultValue="Deutschland"
                  onBlur={(e) => {
                    field.onBlur();
                    setGeocodingError(null);
                    geocodeAddress();
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="coordinates.lat"
              control={control}
              rules={{ required: t('locationForm.latitudeRequired') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('locationForm.latitude')}
                  type="number"
                  fullWidth
                  error={!!errors.coordinates?.lat}
                  helperText={errors.coordinates?.lat?.message}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="coordinates.lng"
              control={control}
              rules={{ required: t('locationForm.longitudeRequired') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('locationForm.longitude')}
                  type="number"
                  fullWidth
                  error={!!errors.coordinates?.lng}
                  helperText={errors.coordinates?.lng?.message}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('locationForm.phone')}
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('locationForm.email')}
                  type="email"
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="website"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('locationForm.website')}
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="donationAccount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('locationForm.donationAccount')}
                  multiline
                  rows={4}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <LocalizedTextFields
                  label={t('locationForm.description')}
                  value={field.value || emptyLocalizedText(languageCodes)}
                  onChange={field.onChange}
                  multiline
                  rows={4}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 3, height: 'fit-content' }}>
              <Controller
                name="image"
                control={control}
                render={({ field }) => (
                  <ImageUpload
                    value={field.value}
                    onChange={(file) => field.onChange(file)}
                    error={errors.image?.message}
                    disabled={mutation.isPending}
                    locationId={id as string | undefined}
                  />
                )}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={7.2}>
            <Paper sx={{ p: 3, height: 'fit-content' }}>
              <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
                {t('locationForm.openingHoursConfiguration')}
              </Typography>

              {activeCategories.length === 0 ? (
                <Typography color="text.secondary">
                  {t('locationForm.noActiveCategories')}
                </Typography>
              ) : (
                activeCategories.map((category, index) => (
                  <Box
                    key={category.key}
                    sx={
                      index > 0
                        ? { mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }
                        : undefined
                    }
                  >
                    <OpeningHoursTable
                      value={location?.categories?.[category.key]
                        ? normalizeOpeningHoursEntries(location.categories[category.key])
                        : []}
                      onChange={() => {}}
                      label={getLocalizedText(category.label, i18n.language, languageCodes)}
                      icon={getCategoryIcon(category.iconName, category.key)}
                      languageCodes={languageCodes}
                      ref={(ref) => {
                        openingHoursRefs.current[category.key] = ref;
                      }}
                    />
                  </Box>
                ))
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4.8}>
            <Paper sx={{ p: 3, height: 'fit-content', position: 'sticky', top: 20 }}>
              <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
                {t('locationForm.accessibilityAndServices')}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Controller
                  name="isAccessible"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                          icon={
                            <Box sx={{ position: 'relative' }}>
                              <AccessibilityIcon />
                            </Box>
                          }
                          checkedIcon={
                            <Box sx={{ position: 'relative' }}>
                              <AccessibilityIcon color="primary" />
                              <CheckIcon
                                sx={{
                                  position: 'absolute',
                                  top: -2,
                                  right: -2,
                                  fontSize: 16,
                                  color: 'white',
                                  backgroundColor: 'primary.main',
                                  borderRadius: '50%',
                                  padding: '1px',
                                }}
                              />
                            </Box>
                          }
                          sx={{
                            '& .MuiSvgIcon-root': {
                              fontSize: 28,
                            },
                            '&.Mui-checked': {
                              color: 'primary.main',
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            },
                          }}
                        />
                      }
                      label={t('locationForm.wheelchairAccessible')}
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '1rem',
                          fontWeight: 500,
                        },
                      }}
                    />
                  )}
                />

                <Controller
                  name="offersForWomen"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                          icon={
                            <Box sx={{ position: 'relative' }}>
                              <WomanIcon />
                            </Box>
                          }
                          checkedIcon={
                            <Box sx={{ position: 'relative' }}>
                              <WomanIcon color="primary" />
                              <CheckIcon
                                sx={{
                                  position: 'absolute',
                                  top: -2,
                                  right: -2,
                                  fontSize: 16,
                                  color: 'white',
                                  backgroundColor: 'primary.main',
                                  borderRadius: '50%',
                                  padding: '1px',
                                }}
                              />
                            </Box>
                          }
                          sx={{
                            '& .MuiSvgIcon-root': {
                              fontSize: 28,
                            },
                            '&.Mui-checked': {
                              color: 'primary.main',
                            },
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            },
                          }}
                        />
                      }
                      label={t('locationForm.servicesForWomen')}
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '1rem',
                          fontWeight: 500,
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/locations')}
              >
                {t('locationForm.cancel')}
              </Button>
              <Button
                type="button"
                variant="contained"
                disabled={mutation.isPending}
                onClick={async () => {
                  await handleSubmit(onSubmit)();
                }}
              >
                {mutation.isPending ? t('locationForm.saving') : t('locationForm.save')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default LocationForm;
